import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Button, Segmented, Select, message } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import API from '@api/api';
import Utils from '@utils/utils';
import { uploadFlowImage } from '../../execution';
import styles from './index.module.scss';

const COUNT_OPTIONS = Array.from({ length: 4 }, (_, index) => ({
    value: index + 1,
    label: `${index + 1}张`,
}));

const MODE_OPTIONS = [
    { label: '单件换色', value: 'single' },
    { label: '套装换色', value: 'suit' },
    { label: '面料换色', value: 'material' },
];

const TARGET_AREA_OPTIONS = [
    { label: '上装', value: 0 },
    { label: '下装', value: 1 },
    { label: '连体装', value: 3 },
];
const RELIGHT_UNIT_COST = 10;
const LINKED_IMAGE_ICON = 'https://photostudio-ai-oss.arcsoft.com.cn/photostudio-opshub/FreeCanvas/node-image.svg';
const FLOW_LINK_PLACEHOLDER_ICON = 'https://photostudio-ai-oss.arcsoft.com.cn/photostudio-opshub/FreeCanvas/node-image.svg';
const beanIcon = require('@assets/images/arcbean3.png');

const isSameValue = (left, right) => JSON.stringify(left) === JSON.stringify(right);

const buildInitialUploads = () => ({
    source: '',
    reference: '',
    match: '',
});

const getVisibleSlotKeysByMode = (mode) => {
    if (mode === 'suit') return ['source', 'reference', 'match'];
    if (mode === 'material') return ['source', 'reference'];
    return ['source', 'reference'];
};

const normalizeTargetArea = (value) => {
    if (value === 1 || value === '1') return 1;
    if (value === 3 || value === '3') return 3;
    return 0;
};

const mapClothTypeToTargetArea = (clothType) => {
    if (clothType === 1 || clothType === '1') return 1;
    if (clothType === 3 || clothType === '3') return 3;
    return 0;
};

const buildRelightPersistPatch = ({
    nodeData,
    uploads,
    mode,
    targetArea,
    generateCount,
    singleClothType,
    visibleSlotKeys,
}) => {
    const currentLinkedSlotSources = (
        nodeData?.panelState?.linkedSlotSources
        && typeof nodeData.panelState.linkedSlotSources === 'object'
        && !Array.isArray(nodeData.panelState.linkedSlotSources)
    )
        ? nodeData.panelState.linkedSlotSources
        : {};
    const nextLinkedSlotSources = Object.fromEntries(
        Object.entries(currentLinkedSlotSources).filter(([slotKey, linkedState]) => (
            visibleSlotKeys.includes(slotKey)
            && (
                (!!linkedState?.edgeId && uploads?.[slotKey] !== '')
                || (uploads?.[slotKey] && uploads[slotKey] === (linkedState?.url || ''))
            )
        )),
    );

    return {
        inputParams: {
            refImage: uploads.source || '',
            srcImage: uploads.reference || '',
            matchImage: mode === 'suit' ? (uploads.match || '') : '',
            mode: mode === 'material' ? 'material' : 'cloth',
            clothType: mode === 'material' ? targetArea : mode === 'single' ? singleClothType : '',
            targetArea,
            num: generateCount,
            skipSelect: 0,
        },
        panelState: {
            uploads,
            linkedSlotSources: nextLinkedSlotSources,
            mode,
            targetArea,
            num: generateCount,
            singleClothType,
            skipSelect: false,
        },
    };
};

const resolveSingleReferenceSuitSlotKey = (targetArea) => (
    normalizeTargetArea(targetArea) === 1 ? 'match' : 'reference'
);

const RelightPanel = ({
    nodeId,
    nodeData,
    onChange,
    disabled = false,
}) => {
    const fileInputRefs = useRef({});
    const dragStartedRef = useRef(false);
    const panelState = nodeData?.panelState || {};
    const inputParams = nodeData?.inputParams || {};
    const [uploads, setUploads] = useState(() => ({
        ...buildInitialUploads(),
        ...(panelState.uploads || {}),
    }));
    const [mode, setMode] = useState(
        panelState.mode
        || panelState?.extraSelectValues?.mode
        || inputParams.mode
        || 'single',
    );
    const [targetArea, setTargetArea] = useState(
        normalizeTargetArea(
            panelState.targetArea ?? panelState?.extraSelectValues?.targetArea ?? inputParams.targetArea ?? 0,
        ),
    );
    const [generateCount, setGenerateCount] = useState(panelState.num ?? inputParams.num ?? 4);
    const [singleClothType, setSingleClothType] = useState(
        panelState.singleClothType ?? inputParams.clothType ?? '',
    );
    const [draggingSlotKey, setDraggingSlotKey] = useState('');
    const [dragOverSlotKey, setDragOverSlotKey] = useState('');
    const lastSubmittedRef = useRef('');
    const skipNextSubmitRef = useRef(false);
    const lastExternalSyncSignatureRef = useRef('');
    const lastClassifiedReferenceUrlRef = useRef('');
    const pendingModeTransitionRef = useRef(false);
    const hasHydratedFromNodeRef = useRef(false);
    const linkedSlotSources = useMemo(() => (
        nodeData?.panelState?.linkedSlotSources
        && typeof nodeData.panelState.linkedSlotSources === 'object'
        && !Array.isArray(nodeData.panelState.linkedSlotSources)
            ? nodeData.panelState.linkedSlotSources
            : {}
    ), [nodeData?.panelState?.linkedSlotSources]);
    const visibleSlotKeys = useMemo(() => getVisibleSlotKeysByMode(mode), [mode]);
    const costBean = useMemo(() => generateCount * RELIGHT_UNIT_COST, [generateCount]);

    useEffect(() => {
        const nextUploads = {
            ...buildInitialUploads(),
            ...(panelState.uploads || {}),
        };
        const nextMode = panelState.mode
            || panelState?.extraSelectValues?.mode
            || inputParams.mode
            || 'single';
        const nextTargetArea = normalizeTargetArea(
            panelState.targetArea ?? panelState?.extraSelectValues?.targetArea ?? inputParams.targetArea ?? 0,
        );
        const nextGenerateCount = panelState.num ?? inputParams.num ?? 4;
        const nextSingleClothType = nextMode === 'single'
            ? (panelState.singleClothType ?? inputParams.clothType ?? '')
            : (panelState.singleClothType ?? '');
        const externalSyncSignature = JSON.stringify({
            uploads: nextUploads,
            mode: nextMode,
            targetArea: nextTargetArea,
            generateCount: nextGenerateCount,
            singleClothType: nextSingleClothType,
        });

        if (lastExternalSyncSignatureRef.current === externalSyncSignature) {
            return;
        }
        lastExternalSyncSignatureRef.current = externalSyncSignature;

        const shouldSyncFromNode = (
            !isSameValue(uploads, nextUploads)
            || mode !== nextMode
            || targetArea !== nextTargetArea
            || generateCount !== nextGenerateCount
            || singleClothType !== nextSingleClothType
        );

        if (shouldSyncFromNode) {
            skipNextSubmitRef.current = true;
        }

        lastClassifiedReferenceUrlRef.current = nextUploads.reference || '';
        lastSubmittedRef.current = JSON.stringify(buildRelightPersistPatch({
            nodeData,
            uploads: nextUploads,
            mode: nextMode,
            targetArea: nextTargetArea,
            generateCount: nextGenerateCount,
            singleClothType: nextSingleClothType,
            visibleSlotKeys: getVisibleSlotKeysByMode(nextMode),
        }));
        hasHydratedFromNodeRef.current = true;

        setUploads((prev) => (isSameValue(prev, nextUploads) ? prev : nextUploads));
        setMode((prev) => (prev === nextMode ? prev : nextMode));
        setTargetArea((prev) => (prev === nextTargetArea ? prev : nextTargetArea));
        setGenerateCount((prev) => (prev === nextGenerateCount ? prev : nextGenerateCount));
        setSingleClothType((prev) => (prev === nextSingleClothType ? prev : nextSingleClothType));
    }, [
        inputParams.clothType,
        inputParams.mode,
        inputParams.num,
        inputParams.targetArea,
        panelState.extraSelectValues,
        panelState.mode,
        panelState.num,
        panelState.singleClothType,
        panelState.targetArea,
        panelState.uploads,
        nodeData,
    ]);

    useEffect(() => {
        if (!hasHydratedFromNodeRef.current) return undefined;
        if (mode !== 'single') return undefined;

        const referenceImageUrl = uploads.reference || '';
        if (!referenceImageUrl) {
            lastClassifiedReferenceUrlRef.current = '';
            setSingleClothType('');
            return undefined;
        }

        if (lastClassifiedReferenceUrlRef.current === referenceImageUrl) {
            return undefined;
        }

        lastClassifiedReferenceUrlRef.current = referenceImageUrl;
        setSingleClothType('');
        let canceled = false;

        const syncClothType = async () => {
            try {
                const result = await API.getClothType({ imageUrl: referenceImageUrl });
                if (canceled || result?.code !== 0) return;
                const nextClothType = result.data ?? '';
                setSingleClothType(nextClothType);
                setTargetArea(mapClothTypeToTargetArea(nextClothType));
            } catch (error) {
                console.error('getClothType error', error);
            }
        };

        syncClothType();

        return () => {
            canceled = true;
        };
    }, [mode, uploads.reference]);

    const handleModeChange = useCallback((nextMode) => {
        if (disabled || !nextMode || nextMode === mode) return;

        pendingModeTransitionRef.current = true;

        if (nextMode === 'material') {
            setUploads((prev) => ({
                ...prev,
                reference: '',
                match: '',
            }));

            setMode(nextMode);
            return;
        }

        if (mode === 'single' && nextMode === 'suit') {
            const targetSlotKey = resolveSingleReferenceSuitSlotKey(targetArea || singleClothType);

            if (targetSlotKey === 'match' && (uploads.reference || linkedSlotSources?.reference?.edgeId)) {
                setUploads((prev) => ({
                    ...prev,
                    reference: '',
                    match: prev.reference || '',
                }));

                if (linkedSlotSources?.reference?.edgeId) {
                    nodeData?.onSwapInputSlots?.(nodeId, 'reference', 'match');
                }
            }

            setMode(nextMode);
            return;
        }

        if (mode === 'suit' && nextMode === 'single') {
            const hasReferenceValue = !!uploads.reference || !!linkedSlotSources?.reference?.edgeId;
            const hasMatchValue = !!uploads.match || !!linkedSlotSources?.match?.edgeId;

            if (!hasReferenceValue && hasMatchValue) {
                setUploads((prev) => ({
                    ...prev,
                    reference: prev.match || '',
                    match: '',
                }));
                setTargetArea(1);
                setSingleClothType(1);

                if (linkedSlotSources?.match?.edgeId) {
                    nodeData?.onSwapInputSlots?.(nodeId, 'match', 'reference');
                }
            } else if (hasMatchValue) {
                setUploads((prev) => ({
                    ...prev,
                    match: '',
                }));
            }

            setMode(nextMode);
            return;
        }

        setMode(nextMode);
    }, [
        disabled,
        linkedSlotSources,
        mode,
        nodeData,
        nodeId,
        singleClothType,
        targetArea,
        uploads.match,
        uploads.reference,
    ]);

    useEffect(() => {
        if (pendingModeTransitionRef.current) {
            pendingModeTransitionRef.current = false;
            return;
        }

        const hiddenSlotKeys = Object.keys(buildInitialUploads()).filter((slotKey) => !visibleSlotKeys.includes(slotKey));
        const shouldReset = hiddenSlotKeys.some((slotKey) => (
            !!uploads[slotKey] || !!linkedSlotSources?.[slotKey]?.edgeId
        ));

        if (!shouldReset) return;

        skipNextSubmitRef.current = true;
        setUploads((prev) => {
            const nextUploads = { ...prev };
            hiddenSlotKeys.forEach((slotKey) => {
                nextUploads[slotKey] = '';
            });
            return nextUploads;
        });

    }, [linkedSlotSources, mode, nodeData, nodeId, uploads, visibleSlotKeys]);

    const handleUploadChange = useCallback((slotKey, nextValue) => {
        setUploads((prev) => ({
            ...prev,
            [slotKey]: nextValue,
        }));
    }, []);

    const isUploadSlotOccupied = useCallback((slotKey) => (
        !!uploads?.[slotKey]
        || !!linkedSlotSources?.[slotKey]?.edgeId
    ), [linkedSlotSources, uploads]);

    const handleUploadDragStart = useCallback((slotKey, event) => {
        if (disabled || !isUploadSlotOccupied(slotKey)) return;
        dragStartedRef.current = true;
        event.dataTransfer.effectAllowed = 'move';
        setDraggingSlotKey(slotKey);
        setDragOverSlotKey('');
    }, [disabled, isUploadSlotOccupied]);

    const handleUploadDragEnd = useCallback(() => {
        window.setTimeout(() => {
            dragStartedRef.current = false;
        }, 0);
        setDraggingSlotKey('');
        setDragOverSlotKey('');
    }, []);

    const handleUploadDragOver = useCallback((slotKey, event) => {
        const hasFile = Array.from(event.dataTransfer?.types || []).includes('Files');
        if (hasFile) {
            event.preventDefault();
            event.stopPropagation();
            event.dataTransfer.dropEffect = 'copy';
            setDragOverSlotKey(slotKey);
            return;
        }

        if (!draggingSlotKey || draggingSlotKey === slotKey) return;
        if (!isUploadSlotOccupied(draggingSlotKey)) return;
        event.preventDefault();
        event.stopPropagation();
        event.dataTransfer.dropEffect = 'move';
        setDragOverSlotKey(slotKey);
    }, [draggingSlotKey, isUploadSlotOccupied]);

    const handleUploadDragLeave = useCallback((slotKey) => {
        setDragOverSlotKey((prev) => (prev === slotKey ? '' : prev));
    }, []);

    const handleUploadDrop = useCallback(async (slotKey, event) => {
        const files = Array.from(event.dataTransfer?.files || []);
        const imageFile = files.find((file) => file?.type?.startsWith('image/'));

        if (imageFile) {
            event.preventDefault();
            event.stopPropagation();
            if (linkedSlotSources?.[slotKey]?.edgeId) {
                message.warning('请先解除连线再上传图片');
                setDragOverSlotKey('');
                return;
            }
            try {
                const fileUrl = await uploadFlowImage(imageFile);
                if (fileUrl) {
                    handleUploadChange(slotKey, fileUrl);
                }
            } catch (error) {
                message.error('上传失败，请稍后重试');
            } finally {
                setDragOverSlotKey('');
            }
            return;
        }

        if (!draggingSlotKey || draggingSlotKey === slotKey) return;
        if (!isUploadSlotOccupied(draggingSlotKey)) return;
        event.preventDefault();
        event.stopPropagation();
        nodeData?.onSwapInputSlots?.(nodeId, draggingSlotKey, slotKey);
        setDraggingSlotKey('');
        setDragOverSlotKey('');
    }, [draggingSlotKey, handleUploadChange, isUploadSlotOccupied, linkedSlotSources, nodeData, nodeId]);

    useEffect(() => {
        if (draggingSlotKey && !isUploadSlotOccupied(draggingSlotKey)) {
            setDraggingSlotKey('');
            setDragOverSlotKey('');
        }
    }, [draggingSlotKey, isUploadSlotOccupied]);

    const handleOpenLocalPicker = useCallback((slotKey, event) => {
        event?.preventDefault?.();
        event?.stopPropagation?.();
        if (disabled) return;

        const inputElement = fileInputRefs.current?.[slotKey];
        if (!inputElement) return;

        if (typeof inputElement.showPicker === 'function') {
            try {
                inputElement.showPicker();
                return;
            } catch (error) {
                // Fall back to click() when showPicker is unavailable or blocked.
            }
        }

        inputElement.click();
    }, [disabled]);

    const handleFileChange = useCallback(async (slotKey, event) => {
        const file = event.target.files?.[0];
        event.target.value = '';

        if (!file || disabled) return;

        try {
            const fileUrl = await uploadFlowImage(file);
            if (fileUrl) {
                handleUploadChange(slotKey, fileUrl);
            }
        } catch (error) {
            message.error('上传失败，请稍后重试');
        }
    }, [disabled, handleUploadChange]);

    const handleRemoveUpload = useCallback((slotKey, event) => {
        event.preventDefault();
        event.stopPropagation();

        const linkedState = linkedSlotSources?.[slotKey];
        if (linkedState?.edgeId) {
            nodeData?.onRemoveLinkedInput?.(nodeId, slotKey, linkedState);
            skipNextSubmitRef.current = true;
        }

        handleUploadChange(slotKey, '');
    }, [handleUploadChange, linkedSlotSources, nodeData, nodeId]);

    const handleGenerate = useCallback(() => {
        if (!uploads.source) {
            message.warning('请先上传模特图');
            return;
        }
        if (mode === 'suit') {
            if (!uploads.reference && !uploads.match) {
                message.warning('请至少上传上装或下装');
                return;
            }
        } else if (!uploads.reference) {
            message.warning(mode === 'material' ? '请先上传面料图' : '请先上传参考图');
            return;
        }

        nodeData?.onExecute?.(nodeId, { count: generateCount });
        nodeData?.onGenerateComplete?.(nodeId);
    }, [generateCount, mode, nodeData, nodeId, uploads.match, uploads.reference, uploads.source]);

    useEffect(() => {
        if (!nodeId || typeof onChange !== 'function') return;
        if (nodeData?.functionKey !== 'relight') return;
        if (!hasHydratedFromNodeRef.current) return;
        if (skipNextSubmitRef.current) {
            skipNextSubmitRef.current = false;
            return;
        }

        const nextPatch = buildRelightPersistPatch({
            nodeData,
            uploads,
            mode,
            targetArea,
            generateCount,
            singleClothType,
            visibleSlotKeys,
        });
        const nextSignature = JSON.stringify(nextPatch);

        if (
            lastSubmittedRef.current === nextSignature
            || (
                isSameValue(nodeData?.inputParams || {}, nextPatch.inputParams)
                && isSameValue(nodeData?.panelState || {}, nextPatch.panelState)
            )
        ) {
            lastSubmittedRef.current = nextSignature;
            return;
        }

        lastSubmittedRef.current = nextSignature;
        onChange(nodeId, nextPatch);
    }, [
        generateCount,
        mode,
        nodeData?.inputParams,
        nodeData?.panelState,
        nodeId,
        onChange,
        singleClothType,
        targetArea,
        uploads,
        visibleSlotKeys,
    ]);

    const uploadCards = useMemo(() => {
        if (mode === 'suit') {
            return [
                { key: 'source', title: '模特图', placeholder: '模特图', sourceCard: true },
                { key: 'reference', title: '上装', placeholder: '上装' },
                { key: 'match', title: '下装', placeholder: '下装' },
            ];
        }

        if (mode === 'material') {
            return [
                { key: 'source', title: '模特图', placeholder: '模特图', sourceCard: true },
                { key: 'reference', title: '面料图', placeholder: '面料图' },
            ];
        }

        return [
            { key: 'source', title: '模特图', placeholder: '模特图', sourceCard: true },
            { key: 'reference', title: '同款不同色衣服图', placeholder: '同款不同色衣服图' },
        ];
    }, [mode]);

    const isGenerating = String(
        nodeData?.latestExecution?.status
        || nodeData?.executionStatus
        || 'idle',
    ).toLowerCase() === 'processing';
    const canGenerate = !disabled
        && !isGenerating
        && (!!uploads.source || !!linkedSlotSources?.source?.edgeId)
        && (
            mode === 'suit'
                ? (
                    !!uploads.reference
                    || !!linkedSlotSources?.reference?.edgeId
                    || !!uploads.match
                    || !!linkedSlotSources?.match?.edgeId
                )
                : (!!uploads.reference || !!linkedSlotSources?.reference?.edgeId)
        );

    return (
        <>
            <div className={`${styles.panel} ${disabled ? styles.panelDisabled : ''}`}>
                <div
                    className={styles.segmentedWrap}
                    onMouseDown={(event) => event.stopPropagation()} 
                    onClick={(event) => event.stopPropagation()}
                >
                    <Segmented
                        block
                        options={MODE_OPTIONS}
                        value={mode}
                        onChange={handleModeChange}
                    />
                </div>
                <div className={styles.uploadRow}>
                    {uploadCards.map((card) => {
                        const linkedState = linkedSlotSources?.[card.key];
                        const previewValue = uploads[card.key] || linkedState?.url || '';
                        const isLinkedInput = !!linkedState?.edgeId;
                        const isLinkedPlaceholder = previewValue === FLOW_LINK_PLACEHOLDER_ICON;
                        const hasResolvedLinkedPreview = isLinkedInput && !!previewValue && !isLinkedPlaceholder;
                        const previewUrl = (
                            previewValue
                            && previewValue !== FLOW_LINK_PLACEHOLDER_ICON
                            && previewValue !== LINKED_IMAGE_ICON
                        ) ? Utils.getImgThumbnail(previewValue) : '';

                        return (
                            <div key={card.key} className={styles.cardColumn}>
                                <div
                                    role="button"
                                    tabIndex={0}
                                    className={[
                                        styles.uploadCard,
                                        card.sourceCard ? styles.sourceCard : styles.referenceCard,
                                        isUploadSlotOccupied(card.key) ? styles.uploadCardDraggable : '',
                                        draggingSlotKey === card.key ? styles.uploadCardDragging : '',
                                        dragOverSlotKey === card.key ? styles.uploadCardDragOver : '',
                                    ].filter(Boolean).join(' ')}
                                    onClick={(event) => {
                                        if (dragStartedRef.current) {
                                            dragStartedRef.current = false;
                                            event.preventDefault();
                                            return;
                                        }
                                        handleOpenLocalPicker(card.key, event);
                                    }}
                                    onMouseDown={(event) => event.stopPropagation()}
                                    draggable={!disabled && isUploadSlotOccupied(card.key)}
                                    onDragStart={(event) => handleUploadDragStart(card.key, event)}
                                    onDragEnd={handleUploadDragEnd}
                                    onDragOver={(event) => handleUploadDragOver(card.key, event)}
                                    onDragLeave={() => handleUploadDragLeave(card.key)}
                                    onDrop={(event) => handleUploadDrop(card.key, event)}
                                >
                                    {previewUrl ? (
                                        <>
                                            <img src={previewUrl} alt={card.title} className={styles.preview} draggable={false} />
                                            <div className={styles.previewTitle}>{card.placeholder}</div>
                                        </>
                                    ) : isLinkedPlaceholder ? (
                                        <div className={styles.linkedPlaceholder}>
                                            <img
                                                src={FLOW_LINK_PLACEHOLDER_ICON}
                                                alt=""
                                                className={styles.linkedPlaceholderIcon}
                                                draggable={false}
                                            />
                                            <span className={styles.linkedPlaceholderLabel}>
                                                {linkedState?.imageSequenceLabel || ''}
                                            </span>
                                        </div>
                                    ) : hasResolvedLinkedPreview ? (
                                        <>
                                            <img src={previewUrl} alt={card.title} className={styles.preview} draggable={false} />
                                            <div className={styles.previewTitle}>{card.placeholder}</div>
                                        </>
                                    ) : isLinkedInput ? (
                                        <div className={styles.linkedPlaceholder}>
                                            <img
                                                src={LINKED_IMAGE_ICON}
                                                alt=""
                                                className={styles.linkedPlaceholderIcon}
                                                draggable={false}
                                            />
                                            <span className={styles.linkedPlaceholderLabel}>
                                                {linkedState?.imageSequenceLabel || ''}
                                            </span>
                                        </div>
                                    ) : (
                                        <div className={styles.emptyState}>
                                            <PlusOutlined className={styles.plusIcon} />
                                            <span className={styles.placeholderText}>{card.placeholder}</span>
                                        </div>
                                    )}
                                    {previewValue ? (
                                        <div className={styles.filledCardActions}>
                                            <button
                                                type="button"
                                                className={`${styles.removeButton} ${(isLinkedPlaceholder || isLinkedInput) ? styles.removeButtonPlaceholder : ''}`}
                                                onClick={(event) => handleRemoveUpload(card.key, event)}
                                                aria-label={`删除${card.title}`}
                                            >
                                                <DeleteOutlined className={styles.removeIcon} />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className={styles.sourceCardActions}>
                                            <Button
                                                size="small"
                                                className={styles.sourceActionButton}
                                                onClick={(event) => handleOpenLocalPicker(card.key, event)}
                                            >
                                                本地上传
                                            </Button>
                                        </div>
                                    )}
                                </div>
                                <input
                                    ref={(node) => {
                                        fileInputRefs.current[card.key] = node;
                                    }}
                                    type="file"
                                    accept="image/*"
                                    className={styles.hiddenInput}
                                    onChange={(event) => handleFileChange(card.key, event)}
                                />
                            </div>
                        );
                    })}
                </div>

                {mode !== 'suit' ? (
                    <div className={styles.selectRow}>
                        <span className={styles.selectLabel}>需换色区域</span>
                        <Select
                            value={targetArea}
                            onChange={setTargetArea}
                            options={TARGET_AREA_OPTIONS}
                            className={styles.areaSelect}
                            popupMatchSelectWidth
                            getPopupContainer={(triggerNode) => triggerNode?.parentElement || triggerNode}
                        />
                    </div>
                ) : null}

                <div className={styles.footer}>
                    <Select
                        value={generateCount}
                        onChange={setGenerateCount}
                        options={COUNT_OPTIONS}
                        popupMatchSelectWidth={false}
                        className={styles.countSelect}
                        variant="borderless"
                        getPopupContainer={(triggerNode) => triggerNode?.parentElement || triggerNode}
                    />
                    <Button
                        type="primary"
                        className={styles.generateButton}
                        onClick={canGenerate ? handleGenerate : undefined}
                        disabled={!canGenerate}
                    >
                        <span className={styles.generateButtonContent}>
                            <span>{isGenerating ? '生成中' : '生成'}</span>
                            {!isGenerating ? <img src={beanIcon} alt="" className={styles.beanIcon} draggable={false} /> : null}
                            {!isGenerating ? <span>{costBean}</span> : null}
                        </span>
                    </Button>
                </div>
            </div>
        </>
    );
};

export default RelightPanel;
